import React, { useState, useEffect } from 'react';
import { openDB } from 'idb';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Alert,
  Divider,
  Grid,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';

const AdminTermsPrivacyEditor: React.FC = () => {
  const { mode } = useCustomTheme();
  const [termsContent, setTermsContent] = useState({
    terms: '',
    privacy: '',
    cookies: '',
    dataProtection: '',
  });
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Load saved content on mount
  useEffect(() => {
    const loadContent = async () => {
      let loadedContent = null;
      
      // Try to load from multiple sources in order of preference
      const sources = [
        () => localStorage.getItem('goatCardsTermsPrivacyContent'),
        () => sessionStorage.getItem('goatCardsTermsPrivacyContent'),
        () => localStorage.getItem('goatCardsTermsPrivacyContent_backup'),
        async () => {
          if ('indexedDB' in window) {
            try {
              const db = await openDB('goatCardsDB', 1);
              return await db.get('content', 'goatCardsTermsPrivacyContent');
            } catch (error) {
              console.warn('IndexedDB load failed:', error);
              return null;
            }
          }
          return null;
        }
      ];
      
      for (const source of sources) {
        try {
          const result = await source();
          if (result) {
            const parsedContent = typeof result === 'string' ? JSON.parse(result) : result;
            if (parsedContent && typeof parsedContent === 'object') {
              loadedContent = parsedContent;
              console.log('Terms & Privacy content loaded from source');
              break;
            }
          }
        } catch (error) {
          console.warn('Failed to load terms & privacy content from source:', error);
          continue;
        }
      }
      
      if (loadedContent) {
        setTermsContent(loadedContent);
        console.log('Terms & Privacy content loaded successfully');
      }
    };
    
    loadContent();
  }, []);

  const handleSave = async () => {
    setSaveStatus('saving');
    try {
      // Save to multiple localStorage keys for redundancy
      localStorage.setItem('goatCardsTermsPrivacyContent', JSON.stringify(termsContent));
      localStorage.setItem('goatCardsTermsPrivacyContent_backup', JSON.stringify(termsContent));
      localStorage.setItem('goatCardsTermsPrivacyContent_timestamp', new Date().toISOString());
      
      // Save to sessionStorage as additional backup
      sessionStorage.setItem('goatCardsTermsPrivacyContent', JSON.stringify(termsContent));
      
      // Save to IndexedDB for persistent storage (if available)
      if ('indexedDB' in window) {
        try {
          const db = await openDB('goatCardsDB', 1, {
            upgrade(db) {
              if (!db.objectStoreNames.contains('content')) {
                db.createObjectStore('content');
              }
            },
          });
          await db.put('content', termsContent, 'goatCardsTermsPrivacyContent');
        } catch (dbError) {
          console.warn('IndexedDB not available, using localStorage only:', dbError);
        }
      }
      
      console.log('Terms & Privacy content saved to multiple storage locations');
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Error saving terms & privacy content:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const isLightMode = mode === 'light';

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: isLightMode ? '#000000' : '#FFFFFF' }}>
          Terms & Privacy Policy Editor
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ color: isLightMode ? '#666666' : '#a0aec0' }}>
          Edit the terms of service and privacy policy that appear on the Terms & Privacy page
        </Typography>
      </Box>

      {saveStatus === 'saved' && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Terms & Privacy content saved successfully!
        </Alert>
      )}

      {saveStatus === 'error' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error saving content. Please try again.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Terms of Service Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: isLightMode ? '#000000' : '#FFFFFF' }}>
              Terms of Service
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: isLightMode ? '#000000' : '#FFFFFF' }}>
              Service Terms
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Terms of Service"
              value={termsContent.terms}
              onChange={(e) => setTermsContent(prev => ({ ...prev, terms: e.target.value }))}
              InputProps={{ sx: { color: isLightMode ? '#000000' : '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: isLightMode ? '#666666' : '#a0aec0' } }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="User Responsibilities"
              value={termsContent.terms}
              onChange={(e) => setTermsContent(prev => ({ ...prev, terms: e.target.value }))}
              InputProps={{ sx: { color: isLightMode ? '#000000' : '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: isLightMode ? '#666666' : '#a0aec0' } }}
            />
          </Paper>
        </Grid>

        {/* Privacy Policy Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: isLightMode ? '#000000' : '#FFFFFF' }}>
              Privacy Policy
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: isLightMode ? '#000000' : '#FFFFFF' }}>
              Data Collection & Use
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Privacy Policy"
              value={termsContent.privacy}
              onChange={(e) => setTermsContent(prev => ({ ...prev, privacy: e.target.value }))}
              InputProps={{ sx: { color: isLightMode ? '#000000' : '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: isLightMode ? '#666666' : '#a0aec0' } }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Information Collection"
              value={termsContent.privacy}
              onChange={(e) => setTermsContent(prev => ({ ...prev, privacy: e.target.value }))}
              InputProps={{ sx: { color: isLightMode ? '#000000' : '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: isLightMode ? '#666666' : '#a0aec0' } }}
            />
          </Paper>
        </Grid>

        {/* Cookies Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: isLightMode ? '#000000' : '#FFFFFF' }}>
              Cookie Policy
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: isLightMode ? '#000000' : '#FFFFFF' }}>
              Cookie Usage
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Cookie Policy"
              value={termsContent.cookies}
              onChange={(e) => setTermsContent(prev => ({ ...prev, cookies: e.target.value }))}
              InputProps={{ sx: { color: isLightMode ? '#000000' : '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: isLightMode ? '#666666' : '#a0aec0' } }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Cookie Management"
              value={termsContent.cookies}
              onChange={(e) => setTermsContent(prev => ({ ...prev, cookies: e.target.value }))}
              InputProps={{ sx: { color: isLightMode ? '#000000' : '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: isLightMode ? '#666666' : '#a0aec0' } }}
            />
          </Paper>
        </Grid>

        {/* Data Protection Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: isLightMode ? '#000000' : '#FFFFFF' }}>
              Data Protection
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: isLightMode ? '#000000' : '#FFFFFF' }}>
              GDPR & Data Rights
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Data Protection Policy"
              value={termsContent.dataProtection}
              onChange={(e) => setTermsContent(prev => ({ ...prev, dataProtection: e.target.value }))}
              InputProps={{ sx: { color: isLightMode ? '#000000' : '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: isLightMode ? '#666666' : '#a0aec0' } }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="User Rights"
              value={termsContent.dataProtection}
              onChange={(e) => setTermsContent(prev => ({ ...prev, dataProtection: e.target.value }))}
              InputProps={{ sx: { color: isLightMode ? '#000000' : '#FFFFFF' } }}
              InputLabelProps={{ sx: { color: isLightMode ? '#666666' : '#a0aec0' } }}
            />
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saveStatus === 'saving'}
          sx={{
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          {saveStatus === 'saving' ? 'Saving...' : 'Save Terms & Privacy'}
        </Button>
      </Box>

      <Box sx={{ mt: 4 }}>
        <Divider sx={{ mb: 3 }} />
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          Changes will be reflected on the Terms & Privacy page immediately after saving.
        </Typography>
      </Box>
    </Container>
  );
};

export default AdminTermsPrivacyEditor; 